/**
 * Cálculo puro de posicionamento de um painel "conectado" a um elemento-gatilho
 * (dropdowns, menus, multi-select). Pensado para painéis com `position: fixed`,
 * portanto trabalha em coordenadas de viewport (`getBoundingClientRect`).
 *
 * Espelha a lógica já validada do `Button` (`setMenuPosition`), mas isolada como
 * função pura para ser testável e reutilizável. Decide o lado (abaixo/acima) com
 * base no espaço disponível, limita a altura ao espaço livre e faz "clamp"
 * horizontal para nunca escapar das bordas da tela.
 */

/** Lado de abertura preferido. `auto` decide pelo espaço disponível. */
export type OverlayPreferred = 'auto' | 'top' | 'bottom';

/** Retângulo mínimo necessário do gatilho (subconjunto de `DOMRect`). */
export interface TriggerRect {
    top: number;
    bottom: number;
    left: number;
    width: number;
}

/** Dimensões do painel a posicionar. */
export interface PanelSize {
    width: number;
    height: number;
}

/** Tamanho da viewport. */
export interface ViewportSize {
    width: number;
    height: number;
}

/** Opções de cálculo. */
export interface OverlayPositionOptions {
    /** Lado preferido. Default `auto`. */
    preferred?: OverlayPreferred;
    /** Espaço entre o gatilho e o painel (px). Default `6`. */
    gap?: number;
    /** Margem mínima até a borda da tela (px). Default `8`. */
    edgePadding?: number;
    /** Altura mínima garantida ao painel (px). Default `120`. */
    minHeight?: number;
}

/** Resultado do posicionamento (coordenadas de viewport, para `position: fixed`). */
export interface OverlayPosition {
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    /** Lado efetivamente escolhido após a verificação de espaço. */
    placement: 'top' | 'bottom';
}

/**
 * Calcula a posição de um painel conectado a um gatilho.
 *
 * @param trigger Retângulo do gatilho (viewport-relative).
 * @param panel Dimensões do painel (largura é ignorada; usa a do gatilho).
 * @param viewport Dimensões da viewport.
 * @param options Preferência de lado, espaçamentos e altura mínima.
 */
export function computeConnectedPosition(
    trigger: TriggerRect,
    panel: PanelSize,
    viewport: ViewportSize,
    options: OverlayPositionOptions = {},
): OverlayPosition {
    const gap = options.gap ?? 6;
    const edgePadding = options.edgePadding ?? 8;
    const minHeight = options.minHeight ?? 120;
    const preferred = options.preferred ?? 'auto';

    const spaceBelow = viewport.height - trigger.bottom - gap;
    const spaceAbove = trigger.top - gap;

    // Prefere o lado pedido; só troca quando não cabe e o outro lado tem mais
    // espaço. No modo `auto`, mantém-se abaixo se couber, senão escolhe o maior.
    let placeBelow: boolean;
    if (preferred === 'bottom') {
        placeBelow = panel.height <= spaceBelow || spaceBelow >= spaceAbove;
    } else if (preferred === 'top') {
        placeBelow = !(panel.height <= spaceAbove || spaceAbove >= spaceBelow);
    } else {
        if (panel.height <= spaceBelow) {
            placeBelow = true;
        } else if (panel.height <= spaceAbove) {
            placeBelow = false;
        } else {
            placeBelow = spaceBelow >= spaceAbove;
        }
    }

    let top: number;
    let maxHeight: number;
    let placement: 'top' | 'bottom';
    if (placeBelow) {
        top = trigger.bottom + gap;
        maxHeight = Math.max(minHeight, spaceBelow - edgePadding);
        placement = 'bottom';
    } else {
        const available = Math.max(minHeight, spaceAbove - edgePadding);
        const used = Math.min(panel.height, available);
        top = trigger.top - used - gap;
        maxHeight = available;
        placement = 'top';
    }

    // Largura do painel acompanha o gatilho; "clamp" horizontal entre as bordas.
    const width = trigger.width;
    const maxLeft = Math.max(edgePadding, viewport.width - width - edgePadding);
    const left = Math.min(Math.max(trigger.left, edgePadding), maxLeft);

    return { top, left, width, maxHeight, placement };
}
